// src/pages/EditPost.js
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';

import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../components/editor/EditorToolbar';
import tiptapExtensions from '../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../components/editor/useEditorWithDragDrop';
import { getPostById, updatePost, uploadPostFiles } from '../../api/boardApi';
import { uploadEditorImage } from '../../api/common';
import FileAttachment from '../../components/FileAttachment/FileAttachment';

function EditPost() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const currentPage = searchParams.get('page') || 1;
  const codeId = searchParams.get('code_id');

  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);

  const pendingImages = useRef(new Map());

  const storeImageLocally = useCallback((file) => {
    const blobUrl = URL.createObjectURL(file);
    pendingImages.current.set(blobUrl, file);
    return blobUrl;
  }, []);

  const editor = useEditorWithDragDrop(tiptapExtensions, '', { onImageUpload: storeImageLocally });

  useEffect(() => {
    if (!editor) return;
    getPostById(id, token)
      .then((res) => {
        //console.log('게시글 데이터:', res);
        setTitle(res.title);

        if (editor && !editor.isDestroyed) {
          editor.commands.setContent(res.content || '');
        }

        // ✅ 기존 파일은 Sequelize dataValues 안에서 꺼내도록 수정
        if (Array.isArray(res.files)) {
          const existingFiles = res.files.map(f => {
            const file = f.dataValues || f; // Sequelize 인스턴스면 dataValues, 아니면 그대로
            return {
              id: file.id,
              name: file.originalName,
              size: file.size,
              type: file.mimeType,
              path: file.path,
              originalName: file.originalName,
              isNew: false,
              isDeleted: false,
            };
          });
          setFiles(existingFiles);
        }

        setLoading(false);
      })
      .catch((err) => {
        console.error('게시글 불러오기 실패:', err);
        alert('게시글을 불러오지 못했습니다.');
        navigate('/board');
      });
  }, [id, navigate, token, editor]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      let content = editor.getHTML();

      for (const [blobUrl, file] of pendingImages.current) {
        if (content.includes(blobUrl)) {
          const serverUrl = await uploadEditorImage(file, token, 'board', id);
          content = content.replaceAll(blobUrl, serverUrl);
        }
        URL.revokeObjectURL(blobUrl);
      }
      pendingImages.current.clear();

      // ✅ 삭제할 파일 id만 수집
      const deletedFileIds = files
        .filter(f => f.isDeleted && f.id != null)
        .map(f => f.id);

      await updatePost(
        id,
        {
          title,
          content,
          code_id: codeId,
          deletedFileIds,
        },
        token
      );

      // 새로 첨부한 파일만 업로드
      const newFiles = files.filter(f => f.isNew);
      if (newFiles.length > 0) {
        const formData = new FormData();
        formData.append('code_id', codeId);
        newFiles.forEach(f => formData.append('files', f.file));
        await uploadPostFiles(id, formData, token);
      }

      navigate(`/board?page=${currentPage}&code_id=${codeId}`);
    } catch (err) {
      console.error('게시글 수정 실패:', err);
      alert('게시글 수정 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="text-center text-muted mt-5">
        <div className="spinner-border text-secondary mb-3" role="status" />
        <p>불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid view-post-edit">
      <div className="row">
        <div className="col-12">
          <form onSubmit={handleUpdate}>
            <div className="mb-3">
              <label className="form-label fw-bold">제목</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">내용</label>
              <div className="border p-2 rounded">
                {editor && <EditorToolbar editor={editor} onImageUpload={storeImageLocally} />}
                <EditorContent editor={editor} className="editor-content" />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">첨부파일</label>
              <FileAttachment files={files} setFiles={setFiles} mode="edit" />
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button type="submit" className="btn btn-success">
                수정 완료
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() =>
                  navigate(`/board?page=${currentPage}&code_id=${codeId}`)
                }
              >
                목록
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditPost;
